import { CreditCard, Wallet, Layers, CalendarClock, Banknote, Landmark, HelpCircle, ReceiptText } from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Reveal";
import { GoldButton } from "@/components/Brand";
import { useEnquiry } from "@/components/EnquiryDialog";
import { PAYMENT_METHODS } from "@/lib/data";

const icons = { CreditCard, Wallet, Layers, CalendarClock, Banknote, Landmark };

// Elegant, editorial hero visual for Payment Options.
// Uses layered gold + navy blocks and a Playfair monogram, tying to brand.
const PaymentHero = () => (
  <div
    className="relative h-[360px] md:h-[460px] overflow-hidden border border-gold/20 bg-navy-surface"
    data-testid="payment-hero-visual"
  >
    <div className="absolute inset-0"
         style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(201,168,118,0.22) 0%, rgba(10,20,32,0) 55%)" }} />
    <div className="absolute -right-10 -top-10 w-64 h-64 rounded-full border border-gold/25" />
    <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full border border-gold/15" />
    <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-12">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] tracking-[0.35em] uppercase text-gold mb-2">Enrolment</p>
          <p className="font-playfair text-white/90 text-2xl md:text-3xl leading-tight max-w-[15ch]">
            Invest in your training with confidence
          </p>
        </div>
        <div className="hidden md:flex items-center justify-center w-14 h-14 border border-gold/40 rounded-full">
          <span className="font-playfair text-gold text-xl leading-none">AA</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 md:gap-6">
        <div className="border border-white/10 p-4 md:p-5">
          <p className="text-[9px] tracking-[0.3em] uppercase text-gold/80 mb-1">Card</p>
          <p className="text-white/70 text-xs md:text-sm">Debit &amp; credit</p>
        </div>
        <div className="border border-white/10 p-4 md:p-5">
          <p className="text-[9px] tracking-[0.3em] uppercase text-gold/80 mb-1">Transfer</p>
          <p className="text-white/70 text-xs md:text-sm">Bank &amp; cash</p>
        </div>
        <div className="border border-gold/40 p-4 md:p-5 bg-gold/[0.04]">
          <p className="text-[9px] tracking-[0.3em] uppercase text-gold mb-1">Flexible</p>
          <p className="text-white/85 text-xs md:text-sm">Klarna · Clearpay · Pay It Later</p>
        </div>
      </div>
    </div>
  </div>
);

export default function Payment() {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      <section className="relative pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <Overline className="mb-5 block">Payment Options</Overline>
            <MaskedLines
              className="font-playfair uppercase leading-[0.95] tracking-tight"
              lineClassName="text-5xl md:text-7xl"
              lines={[<span key="1" className="text-gold">Invest In</span>, <span key="2" className="text-white">Your Future</span>]}
            />
            <div className="hairline w-40 my-8" />
            <p className="text-white/65 text-lg leading-relaxed mb-4">We believe investing in your education should be simple and flexible.</p>
            <p className="text-white/65 text-lg leading-relaxed">That&apos;s why we offer a range of payment options to suit your needs.</p>
          </div>
          <Reveal delay={0.2}>
            <PaymentHero />
          </Reveal>
        </div>
      </section>

      <section className="py-8 bg-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-center gap-6">
          <span className="hairline w-16" />
          <Overline>Available Payment Methods</Overline>
          <span className="hairline w-16" />
        </div>
      </section>

      <section className="py-16 bg-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-12 gap-x-4">
          {PAYMENT_METHODS.map((m, i) => {
            const Icon = icons[m.icon];
            return (
              <Reveal key={m.name} delay={i * 0.06} className="text-center px-3">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full border border-gold/40 flex items-center justify-center">
                  <Icon size={30} strokeWidth={1.2} className="text-gold" />
                </div>
                <h3 className="font-manrope text-sm tracking-[0.12em] uppercase text-white mb-3">{m.name}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{m.text}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-16 bg-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="border border-white/12 bg-navy-surface p-8 md:p-12 max-w-3xl mx-auto">
            <ReceiptText size={34} strokeWidth={1} className="text-gold mb-6" />
            <h3 className="font-playfair text-2xl md:text-3xl text-gold mb-5 tracking-wide">Flexible Payment Options</h3>
            <p className="text-white/70 mb-6 leading-relaxed">
              We offer a range of flexible payment options to help you spread the cost of your training, subject to eligibility and the terms of the individual payment provider.
            </p>
            <div className="hairline w-full mb-6" />
            <p className="text-white/55 text-sm leading-relaxed">
              Flexible payment options are available on selected courses, subject to eligibility and approval. Where a finance or payment-plan option is selected, the total amount payable may be higher than the standard course fee. All applicable costs, instalments and payment terms will be clearly disclosed before you enter into any finance or payment agreement. Finance and payment plan options are provided subject to the individual provider&apos;s terms, conditions and eligibility criteria. Please ensure you understand the total amount payable and repayment terms before entering into an agreement.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-navy border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 shrink-0 rounded-full border border-gold/40 flex items-center justify-center">
              <HelpCircle size={28} strokeWidth={1.2} className="text-gold" />
            </div>
            <div>
              <h3 className="font-playfair text-2xl text-gold tracking-wide mb-1">Questions?</h3>
              <p className="text-white/60 max-w-md">Our team is here to help with any questions about course fees, enrolment payments and the payment options available to you.</p>
            </div>
          </div>
          <GoldButton onClick={() => openEnquiry()} data-testid="payment-enquire-button">Get In Touch</GoldButton>
        </div>
      </section>
    </>
  );
}
